import Component from "../../lib/Component.js";

/**
 * @typedef {{
 *   id: number,
 *   slug: string,
 *   delayed: boolean,
 *   soon: boolean,
 *   poster: string,
 *   distributor: string,
 *   release_date: string,
 *   title: string,
 *   age_limit: string,
 *   original_title: string,
 *   year: number,
 *   subtitle: string|undefined,
 *   top_country: string,
 *   genres: string,
 *   runtime: number
 * }} MovieListItem
 */

/**
 * @typedef {{
 *  inverse: boolean,
 *  selectable: boolean
 * }} MovieListOptions
 */

/**
 * @typedef {{
 *  el: HTMLElement|null,
 *  addMovie: (movie: MovieListItem) => void,
 *  reset: () => void,
 *  setMessage: (message: string) => void,
 *  setSelectedItem: (item: HTMLElement|null) => void,
 *  getSelectedItem: () => HTMLElement|null,
 *  selectNext: () => void,
 *  selectPrevious: () => void
 * } & Component} MovieListComponent
 */

/**
 * @param {HTMLElement} element
 * @param {HTMLElement} messageElement
 * @param {MovieListOptions} options
 * @returns {MovieListComponent}
 */
const MovieList = (element, messageElement, options) => {
  const el = element;
  const messageEl = messageElement;
  let selectedItem = null;
  const opts = {
    inverse: false,
    selectable: false,
    ...options,
  };

  const CHECKBOX_CLASS = "select-movie-checkbox";

  const STREAMING_DISTRIBUTORS = [
    "HBO GO",
    "HBO Max",
    "Cinego",
    "TVGO",
    "Vimeo",
    "Netflix",
    "Disney+",
    "Amazon Prime",
    "AppleTV+",
    "SkyShowtime",
  ];

  const isStreamingDistributor = (distributor) => {
    return new RegExp(
      STREAMING_DISTRIBUTORS.map((d) => d.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")).join("|"),
      "i"
    ).test(distributor);
  };

  /**
   * Check if a movie is an event based on its genres
   * @param {Array<string>} genres - Array of genre names
   * @returns {boolean}
   */
  const isEventMovie = (genres) => {
    if (!genres || !Array.isArray(genres)) return false;
    return genres.includes("Event");
  };

  const renderListItem = (movie) => {
    let ageLimitColorClass = "";
    if (movie.age_limit || movie.age_limit == "0") {
      if (movie.age_limit.match(/0|kn/i)) ageLimitColorClass = "rating-green";
      else if (movie.age_limit.match(/18|x/i)) ageLimitColorClass = "rating-red";
      else ageLimitColorClass = "rating-yellow";
    }

    const isEvent = isEventMovie(movie.raw_genres);
    const isStreaming = isStreamingDistributor(movie.distributor);

    const _distributorClass = isEvent ? "events" : isStreaming ? "streaming" : "";
    const streamingDistributor = isStreaming ? `<span class="distributor streaming">${movie.distributor}</span>` : "";

    return `
      ${opts.selectable ? `<input type="checkbox" class="${CHECKBOX_CLASS}">` : ""}
      <a href="/movie/${movie.id}-${movie.slug}" 
        class="movie${movie.delayed ? " delayed" : ""}${movie.soon ? " soon" : ""}"
        aria-label="Tovább a film részletes adatlapjára...">
        <picture>
          <source srcset="${movie.poster}">
          <img src="${movie.poster}" alt="" loading="lazy">
        </picture>
        <span class="item-data">
          <span class="item-content">
            ${movie.soon ? `<span class="premier-date${ageLimitColorClass ? ` ${ageLimitColorClass}` : ""}">${streamingDistributor}Hamarosan!</span>` : ""}
            ${movie.delayed ? `<span class="premier-date delayed${ageLimitColorClass ? ` ${ageLimitColorClass}` : ""}">${streamingDistributor}<i class="fas fa-exclamation-triangle"></i> Elhalasztva</span>` : ""}
            ${!movie.soon && !movie.delayed ? `<span class="premier-date${ageLimitColorClass ? ` ${ageLimitColorClass}` : ""}">${streamingDistributor}${movie.release_date}</span>` : ""}
          </span>
          <strong class="item-title">
            ${movie.title}
            ${movie.subtitle ? `<span> &ndash; ${movie.subtitle}</span>` : ""}
          </strong>
          <span class="item-subtitle">
            ${movie.year} &#x2022; ${movie.age_limit || movie.age_limit == "0" ? `(${movie.age_limit}) &#x2022; ` : ""}
            ${movie.top_country} ${movie.genres}, ${movie.runtime} perc
          </span>
        </span>
      </a>
    `;
  };

  /**
   * Scroll container to ensure item is visible
   * @param {HTMLElement|null} item - The item to scroll to, or null to scroll to top
   */
  const scrollToItem = (item) => {
    if (!el?.parentNode) return;

    const container = el.parentNode;

    // Scroll to top if no item specified
    if (!item) {
      container.scrollTop = 0;
      return;
    }

    const containerTop = container.offsetTop + container.scrollTop;
    const containerBottom = containerTop + container.offsetHeight;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;

    // Scroll down if item is below viewport
    if (containerBottom < itemBottom) {
      container.scrollTop = itemBottom - container.offsetTop - container.offsetHeight;
    }
    // Scroll up if item is above viewport
    else if (containerTop > itemTop) {
      container.scrollTop = itemTop - container.offsetTop;
    }
  };

  /**
   * Navigate through selectable items
   * @param {number} direction - 1 for next, -1 for previous
   * @returns {HTMLElement|undefined}
   */
  const selectItemByDirection = (direction) => {
    if (!opts.selectable || !el) return;

    const checkboxes = el.querySelectorAll(`.${CHECKBOX_CLASS}`);
    if (checkboxes.length === 0) return;

    const checkboxArray = Array.from(checkboxes);
    const currentIndex = checkboxArray.findIndex((cb) => cb.checked);
    const nextIndex = currentIndex === -1 ? (direction > 0 ? 0 : checkboxArray.length - 1) : currentIndex + direction;

    // Clear selection if moving beyond boundaries
    if (nextIndex < 0 || nextIndex >= checkboxArray.length) {
      if (currentIndex !== -1) checkboxArray[currentIndex].checked = false;
      component.setSelectedItem(null);
      return;
    }

    // Uncheck current, check next
    if (currentIndex !== -1) checkboxArray[currentIndex].checked = false;
    checkboxArray[nextIndex].checked = true;
    component.setSelectedItem(checkboxArray[nextIndex].nextElementSibling);
  };

  /** @type {MovieListComponent} */
  const component = el
    ? new Component({
        componentName: "MovieList",
        onMount: () => {
          if (opts.inverse) {
            el.classList.add("inverse");
            if (messageEl) messageEl.classList.add("inverse");
          }
        },
        onUnmount: () => {
          el.classList.remove("inverse");
          if (messageEl) messageEl.classList.remove("inverse");
        },
      })
    : Component.noop();

  component.el = el;

  /**
   * @param {MovieListItem} movie
   */
  component.addMovie = (movie) => {
    if (el) {
      let listItem = document.createElement("li");
      listItem.innerHTML = renderListItem(movie);
      el.appendChild(listItem);
    }
  };

  component.reset = () => {
    if (el) el.innerHTML = "";
    component.setSelectedItem(null);
  };

  component.setMessage = (message) => {
    if (messageEl) {
      // Create a temporary element to normalize both HTML strings for comparison
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = message;
      const normalizedMessage = tempDiv.innerHTML;

      if (messageEl.innerHTML !== normalizedMessage) {
        messageEl.innerHTML = message;
      }
    }
  };

  component.setSelectedItem = (newSelectedItem) => {
    selectedItem = newSelectedItem;
    scrollToItem(selectedItem);
  };

  component.getSelectedItem = () => {
    return selectedItem;
  };

  component.selectNext = () => selectItemByDirection(1);

  component.selectPrevious = () => selectItemByDirection(-1);

  return component;
};

export default MovieList;
